import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Save, Shield, Undo2, Zap } from 'lucide-react';
import { adminRiskConfig } from '@/api/admin';
import { RiskApplyNowDialog } from '@/features/risk/RiskApplyNowDialog';
import type { RiskConfig as RC } from '@/api/extras';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Pill } from '@/components/widgets/Pill';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { toast } from '@/components/ui/sonner';
import { ApiError } from '@/lib/http';
import { fmtDateTime } from '@/lib/format';

const RULE_ORDER = [
  '紧急停机 flag 检查',
  '交易时段检查',
  '强平锁定期检查',
  '账户 follow_mode 检查',
  '品种白名单检查',
  '毛持仓 / 单边压力超线只触发后台预警',
  '下单后真实净仓预计保证金占用 < max_margin_usage_percent',
  '净值水位 > equity_floor_percent',
];

export function RiskConfig() {
  const qc = useQueryClient();
  const cfgQ = useQuery({ queryKey: ['admin', 'risk-config'], queryFn: adminRiskConfig.get });

  const [draft, setDraft] = useState<RC | null>(null);
  const [note, setNote] = useState('');
  const [revokeOpen, setRevokeOpen] = useState(false);
  const [applyOpen, setApplyOpen] = useState(false);

  useEffect(() => {
    if (cfgQ.data) setDraft(cfgQ.data);
  }, [cfgQ.data]);

  const revoke = useMutation({
    mutationFn: () => adminRiskConfig.revokePending(),
    onSuccess: () => {
      toast.success('已撤销待生效变更', { description: '风控配置的待生效改动已清除' });
      setRevokeOpen(false);
      qc.invalidateQueries({ queryKey: ['admin', 'risk-config'] });
    },
    onError: (err) => toast.error('撤销失败', { description: err instanceof ApiError ? err.message : '请稍后重试' }),
  });

  const dirty = useMemo(() => {
    if (!draft || !cfgQ.data) return false;
    return (
      draft.maxMarginUsagePercent !== cfgQ.data.maxMarginUsagePercent ||
      draft.maxDailyLossPercent !== cfgQ.data.maxDailyLossPercent ||
      draft.equityFloorPercent !== cfgQ.data.equityFloorPercent ||
      draft.apiErrorRateThreshold !== cfgQ.data.apiErrorRateThreshold ||
      draft.userDailyLossEnabled !== cfgQ.data.userDailyLossEnabled ||
      draft.userDailyLossCloseUsd !== cfgQ.data.userDailyLossCloseUsd ||
      draft.userDailyLossFreezeUsd !== cfgQ.data.userDailyLossFreezeUsd ||
      draft.userDailyProfitEnabled !== cfgQ.data.userDailyProfitEnabled ||
      draft.userDailyProfitCloseUsd !== cfgQ.data.userDailyProfitCloseUsd ||
      draft.userDailyProfitFreezeUsd !== cfgQ.data.userDailyProfitFreezeUsd ||
      draft.userMaxPositionLots100 !== cfgQ.data.userMaxPositionLots100 ||
      draft.userDailyMaxTrades !== cfgQ.data.userDailyMaxTrades ||
      draft.manualCloseMinProfitUsd !== cfgQ.data.manualCloseMinProfitUsd
    );
  }, [draft, cfgQ.data]);

  const submit = useMutation({
    mutationFn: () => {
      if (!draft) throw new Error('no draft');
      return adminRiskConfig.update({
        maxMarginUsagePercent: draft.maxMarginUsagePercent,
        maxDailyLossPercent: draft.maxDailyLossPercent,
        equityFloorPercent: draft.equityFloorPercent,
        apiErrorRateThreshold: draft.apiErrorRateThreshold,
        userDailyLossEnabled: draft.userDailyLossEnabled,
        userDailyLossCloseUsd: draft.userDailyLossCloseUsd,
        userDailyLossFreezeUsd: draft.userDailyLossFreezeUsd,
        userDailyProfitEnabled: draft.userDailyProfitEnabled,
        userDailyProfitCloseUsd: draft.userDailyProfitCloseUsd,
        userDailyProfitFreezeUsd: draft.userDailyProfitFreezeUsd,
        userMaxPositionLots100: draft.userMaxPositionLots100,
        userDailyMaxTrades: draft.userDailyMaxTrades,
        manualCloseMinProfitUsd: draft.manualCloseMinProfitUsd,
        note: note.trim() || undefined,
      });
    },
    onSuccess: () => {
      toast.success('已保存', { description: '将于次日 03:05 生效(紧急停机仍可立即触发)' });
      qc.invalidateQueries({ queryKey: ['admin', 'risk-config'] });
      setNote('');
    },
    onError: (err) => toast.error('保存失败', { description: err instanceof ApiError ? err.message : '' }),
  });

  if (!draft) return null;

  return (
    <div className="p-6 space-y-4">
      <header>
        <h1 className="text-xl font-bold text-ink-0 flex items-center gap-2">
          <Shield className="h-5 w-5 text-brand" />
          风控规则
        </h1>
        <p className="text-sm text-ink-2 mt-1">
          阈值参数 + 飞书告警群(自建应用)· 修改默认次日 03:05 生效(§9.11)
        </p>
      </header>

      {cfgQ.data?.pendingChange && (
        <div className="rounded-md border border-warn/40 bg-warn/10 px-4 py-3 flex items-start gap-3">
          <div className="flex-1 text-sm text-warn-dim space-y-1.5">
            <div className="flex items-center justify-between gap-2 font-semibold">
              <div className="flex items-center gap-2">
                <span>有待生效变更</span>
                <Pill tone="warn">
                  生效:
                  {cfgQ.data.pendingChange.effectiveAt
                    ? fmtDateTime(cfgQ.data.pendingChange.effectiveAt)
                    : '次日 03:05'}
                </Pill>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="warn"
                  size="sm"
                  onClick={() => setApplyOpen(true)}
                  disabled={revoke.isPending}
                >
                  <Zap className="h-3.5 w-3.5" />
                  立即生效
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setRevokeOpen(true)}
                  disabled={revoke.isPending}
                >
                  <Undo2 className="h-3.5 w-3.5" />
                  撤销待生效
                </Button>
              </div>
            </div>
            <PendingRiskDiff current={cfgQ.data} pending={cfgQ.data.pendingChange} />
            {cfgQ.data.pendingChange.note && (
              <div className="text-xs text-ink-2">备注:{cfgQ.data.pendingChange.note}</div>
            )}
          </div>
        </div>
      )}

      <section>
        <Card>
          <CardHeader>
            <CardTitle>阈值参数</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <SliderRow
              label="保证金占用上限"
              hint=">  超出后跟单引擎拒绝新单"
              unit="%"
              min={5}
              max={90}
              step={1}
              value={draft.maxMarginUsagePercent}
              onChange={(v) => setDraft({ ...draft, maxMarginUsagePercent: v })}
            />
            <SliderRow
              label="单日最大亏损"
              hint="0 = 不启用; >0 时当日亏损超限触发紧急停机"
              unit="%"
              min={0}
              max={20}
              step={0.5}
              value={draft.maxDailyLossPercent}
              onChange={(v) => setDraft({ ...draft, maxDailyLossPercent: v })}
              decimals={1}
              zeroLabel="未设置"
            />
            <SliderRow
              label="净值水位下限"
              hint="0 = 不启用; >0 时净值跌穿下限触发紧急停机"
              unit="%"
              min={0}
              max={95}
              step={1}
              value={draft.equityFloorPercent}
              onChange={(v) => setDraft({ ...draft, equityFloorPercent: v })}
              zeroLabel="未设置"
            />
            <SliderRow
              label="API 错误率阈值"
              hint="> 近2分钟滑窗错误率(样本足够时);超出即自动紧急停机"
              unit="%"
              min={1}
              max={50}
              step={1}
              value={draft.apiErrorRateThreshold}
              onChange={(v) => setDraft({ ...draft, apiErrorRateThreshold: v })}
            />
            <PlatformGateWarning
              lossDisabled={draft.maxDailyLossPercent <= 0}
              equityDisabled={draft.equityFloorPercent <= 0}
            />
          </CardContent>
        </Card>

        {/* §6.4.1 逐用户当日盈亏限制 — 四金额:盈亏各「平仓额 + 冻结额」;≤0 关闭;冻结额须 ≥ 平仓额 */}
        <Card className="mt-4">
          <CardHeader>
            <CardTitle>逐用户当日盈亏限制(四金额)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* ── 亏损侧 ── */}
            <div className="space-y-4">
              <div className="text-xs font-semibold text-down">
                亏损侧 · 浮亏达平仓额 → 平仓(不冻结);当日净亏达冻结额 → 平仓 + 当日冻结
              </div>
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-0.5">
                  <Label htmlFor="user-daily-loss-enabled">启用逐用户亏损上限</Label>
                  <p className="text-[11px] text-ink-3 leading-relaxed">
                    冻结 = 当日禁交易,次日自动恢复;后台解除后从解除时刻重新累计(盈亏对称)
                  </p>
                </div>
                <Switch
                  id="user-daily-loss-enabled"
                  checked={draft.userDailyLossEnabled}
                  onCheckedChange={(v) => setDraft({ ...draft, userDailyLossEnabled: v })}
                />
              </div>
              <div className="flex flex-wrap gap-x-8 gap-y-3">
                <div className="space-y-1.5">
                  <Label htmlFor="loss-close">亏损平仓额($)· 持仓浮亏合计</Label>
                  <Input
                    id="loss-close"
                    type="number"
                    min={0}
                    step="1"
                    className="w-44 num"
                    value={draft.userDailyLossCloseUsd}
                    disabled={!draft.userDailyLossEnabled}
                    onChange={(e) =>
                      setDraft({ ...draft, userDailyLossCloseUsd: Number(e.target.value) })
                    }
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="loss-freeze">亏损冻结额($)· 当日累计净亏 · 须 ≥ 平仓额</Label>
                  <Input
                    id="loss-freeze"
                    type="number"
                    min={0}
                    step="1"
                    className="w-44 num"
                    value={draft.userDailyLossFreezeUsd}
                    disabled={!draft.userDailyLossEnabled}
                    onChange={(e) =>
                      setDraft({ ...draft, userDailyLossFreezeUsd: Number(e.target.value) })
                    }
                  />
                </div>
              </div>
              <p className="text-[11px] text-ink-3">金额 ≤0 关闭对应项</p>
            </div>

            {/* ── 盈利侧 ── */}
            <div className="space-y-4 pt-5 border-t border-line">
              <div className="text-xs font-semibold text-up">
                盈利侧 · 浮盈达平仓额 → 平仓落袋(不冻结);当日净盈达冻结额 → 平仓 + 冻结
              </div>
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-0.5">
                  <Label htmlFor="user-daily-profit-enabled">启用逐用户盈利上限</Label>
                  <p className="text-[11px] text-ink-3 leading-relaxed">
                    平仓额 = 止盈落袋可反复;冻结额 = 0 则不启用盈利冻结(只止盈不冻结)
                  </p>
                </div>
                <Switch
                  id="user-daily-profit-enabled"
                  checked={draft.userDailyProfitEnabled}
                  onCheckedChange={(v) => setDraft({ ...draft, userDailyProfitEnabled: v })}
                />
              </div>
              <div className="flex flex-wrap gap-x-8 gap-y-3">
                <div className="space-y-1.5">
                  <Label htmlFor="profit-close">盈利平仓额($)· 持仓浮盈合计</Label>
                  <Input
                    id="profit-close"
                    type="number"
                    min={0}
                    step="1"
                    className="w-44 num"
                    value={draft.userDailyProfitCloseUsd}
                    disabled={!draft.userDailyProfitEnabled}
                    onChange={(e) =>
                      setDraft({ ...draft, userDailyProfitCloseUsd: Number(e.target.value) })
                    }
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="profit-freeze">盈利冻结额($)· 当日累计净盈 · 0=停用</Label>
                  <Input
                    id="profit-freeze"
                    type="number"
                    min={0}
                    step="1"
                    className="w-44 num"
                    value={draft.userDailyProfitFreezeUsd}
                    disabled={!draft.userDailyProfitEnabled}
                    onChange={(e) =>
                      setDraft({ ...draft, userDailyProfitFreezeUsd: Number(e.target.value) })
                    }
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* §6.4.2 逐用户交易 / 持仓限制 + 手动平仓利润门槛(下单闸,任一 ≤0 关闭) */}
        <Card className="mt-4">
          <CardHeader>
            <CardTitle>逐用户交易 / 持仓限制</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-x-8 gap-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="user-max-position">最大持仓(手)</Label>
                <Input
                  id="user-max-position"
                  type="number"
                  min={0}
                  step="0.01"
                  className="w-40 num"
                  value={draft.userMaxPositionLots100 / 100}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      userMaxPositionLots100: Math.round(Number(e.target.value) * 100),
                    })
                  }
                />
                <p className="text-[11px] text-ink-3">全部持仓毛量合计(0.1空+0.1多=0.2);≤0 关闭</p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="user-max-trades">每日最多开仓笔数</Label>
                <Input
                  id="user-max-trades"
                  type="number"
                  min={0}
                  step="1"
                  className="w-40 num"
                  value={draft.userDailyMaxTrades}
                  onChange={(e) =>
                    setDraft({ ...draft, userDailyMaxTrades: Math.round(Number(e.target.value)) })
                  }
                />
                <p className="text-[11px] text-ink-3">当日成功开仓达此数拒新单;≤0 关闭</p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="manual-close-min">手动平仓 / 下单止盈门槛($)</Label>
                <Input
                  id="manual-close-min"
                  type="number"
                  min={0}
                  step="1"
                  className="w-40 num"
                  value={draft.manualCloseMinProfitUsd}
                  onChange={(e) =>
                    setDraft({ ...draft, manualCloseMinProfitUsd: Number(e.target.value) })
                  }
                />
                <p className="text-[11px] text-ink-3">浮盈 (0,门槛) 禁手动平;≤0 关闭</p>
              </div>
            </div>
            <p className="text-[11px] text-ink-3">
              手动平仓门槛:亏损仓 / 浮盈 ≥ 门槛放行;下单所设止盈预期盈利 &lt; 门槛拒单。风控自动止盈 / 止损 /
              冻结不受限。
            </p>
          </CardContent>
        </Card>
        {/* 「飞书告警群」面板已隐藏:web 配置的 chat_id 尚未接入自建应用推送
            (notifier dispatch 仍把 risk.alert_recipients 当 webhook URL 处理,app 模式只发 .env 默认群),
            当前告警统一发往后端 .env 的 FEISHU_CHAT_ID 默认群。待 dispatch 支持遍历 chat_id 逐个 send_card 后再恢复本面板。 */}
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>变更摘要</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="risk-note">备注(写入 audit_logs)</Label>
              <Input id="risk-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="变更原因 / 工单号" disabled={submit.isPending} />
            </div>
            <div className="flex items-center justify-end gap-2 pt-2 border-t border-line">
              <Button variant="outline" disabled={!dirty || submit.isPending} onClick={() => cfgQ.data && setDraft(cfgQ.data)}>
                重置
              </Button>
              <Button onClick={() => submit.mutate()} disabled={!dirty || submit.isPending}>
                <Save className="h-4 w-4" />
                {submit.isPending ? '保存中...' : '保存待生效'}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>跟单引擎 8 步检查顺序</CardTitle>
          </CardHeader>
          <CardContent>
            <ol className="space-y-1.5 text-xs">
              {RULE_ORDER.map((rule, i) => {
                // 净值水位下限设为 0(不启用)时,pre_check 第 8 步不再据此阻单 —— 标注为已停用,避免清单误导。
                const floorDisabled =
                  rule.includes('equity_floor_percent') && (cfgQ.data?.equityFloorPercent ?? 70) <= 0;
                return (
                  <li key={rule} className="flex items-start gap-2">
                    <span className="num text-ink-3 font-mono">{String(i + 1).padStart(2, '0')}.</span>
                    <span className={floorDisabled ? 'text-ink-3 line-through' : 'text-ink-1'}>{rule}</span>
                    {floorDisabled && (
                      <span className="text-[10px] text-ink-3 whitespace-nowrap">· 已停用(下限=0)</span>
                    )}
                  </li>
                );
              })}
            </ol>
            <div className="mt-3 pt-3 border-t border-line text-[11px] text-ink-3 leading-relaxed">
              任一步骤失败,信号写入 <span className="num text-ink-2">signals.skip_reason</span>,
              不下单 · 仅本配置生效后第 6/7/8 步以新阈值判定
            </div>
          </CardContent>
        </Card>
      </section>

      {/* ── 撤销待生效二次确认 Dialog ── */}
      <Dialog open={revokeOpen} onOpenChange={(v) => !v && !revoke.isPending && setRevokeOpen(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-warn">
              <Undo2 className="h-5 w-5" />
              撤销待生效变更
            </DialogTitle>
            <DialogDescription>
              将清除风控配置当前所有<strong>待生效(次日 03:05 生效)</strong>的改动,恢复为当前生效配置。此操作不影响已生效的阈值。
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevokeOpen(false)} disabled={revoke.isPending}>
              取消
            </Button>
            <Button variant="warn" onClick={() => revoke.mutate()} disabled={revoke.isPending}>
              {revoke.isPending ? '撤销中…' : '确认撤销'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <RiskApplyNowDialog open={applyOpen} onOpenChange={setApplyOpen} />
    </div>
  );
}

/**
 * 平台级急停闸门停用提示。maxDailyLossPercent / equityFloorPercent 设为 0 即关闭对应的平台级自动急停
 * (DAILY_LOSS / EQUITY_LOW)。两项同时为 0 时,主账户不再有任何平台级自动停机 / 强平,仅剩逐用户当日
 * 亏损闸 + 容量保证金档兜底 —— 保存 / 立即生效前给运营一次醒目提示,避免无意中裸奔。
 */
function PlatformGateWarning({
  lossDisabled,
  equityDisabled,
}: {
  lossDisabled: boolean;
  equityDisabled: boolean;
}) {
  if (!lossDisabled && !equityDisabled) return null;
  const both = lossDisabled && equityDisabled;
  return (
    <div className="rounded-md border border-warn/40 bg-warn/10 px-3 py-2.5 flex items-start gap-2">
      <AlertTriangle className="h-4 w-4 text-warn shrink-0 mt-0.5" />
      <div className="text-[11px] text-warn-dim leading-relaxed">
        {both ? (
          <>
            <span className="font-semibold">平台级自动急停已全部关闭:</span>
            单日最大亏损与净值水位下限均为 0,主账户不再自动停机 / 强平,仅逐用户当日亏损闸与容量保证金档生效。
          </>
        ) : (
          <>
            <span className="font-semibold">已关闭一项平台级急停:</span>
            {lossDisabled ? '「单日最大亏损」' : '「净值水位下限」'}设为 0 不启用,该项不再触发紧急停机 / 阻单。
          </>
        )}
      </div>
    </div>
  );
}

/**
 * 风控待生效 diff(#21,只读展示)。
 * 后端在有待生效项时返回 pendingChange,含各字段待生效值 + effectiveAt。
 * 这里逐字段对比「当前生效值 → 待生效值」,只渲染真正发生变化的字段。
 * 后端可能只回填变更过的字段,故 != null 才参与对比。
 */
function PendingRiskDiff({ current, pending }: { current: RC; pending: RC['pendingChange'] }) {
  if (!pending) return null;
  const rows: Array<[string, string, string]> = [];
  const fmtOptional = (v: number, unit: string, decimals: number, zeroLabel?: string) =>
    zeroLabel && v <= 0 ? zeroLabel : `${v.toFixed(decimals)}${unit}`;
  const pushNum = (
    label: string,
    cur: number,
    next?: number,
    unit = '%',
    decimals = 0,
    zeroLabel?: string,
  ) => {
    if (next == null || next === cur) return;
    rows.push([
      label,
      fmtOptional(cur, unit, decimals, zeroLabel),
      fmtOptional(next, unit, decimals, zeroLabel),
    ]);
  };
  const pushUsd = (label: string, cur: number, next?: number) => {
    if (next == null || next === cur) return;
    rows.push([label, `$${cur.toFixed(0)}`, `$${next.toFixed(0)}`]);
  };
  const pushBool = (label: string, cur: boolean, next?: boolean) => {
    if (next == null || next === cur) return;
    rows.push([label, cur ? '开' : '关', next ? '开' : '关']);
  };
  pushNum('保证金占用上限', current.maxMarginUsagePercent, pending.maxMarginUsagePercent);
  pushNum('单日最大亏损', current.maxDailyLossPercent, pending.maxDailyLossPercent, '%', 1, '未设置');
  pushNum('净值水位下限', current.equityFloorPercent, pending.equityFloorPercent, '%', 0, '未设置');
  pushNum('API 错误率阈值', current.apiErrorRateThreshold, pending.apiErrorRateThreshold);
  pushBool('逐用户当日亏损上限', current.userDailyLossEnabled, pending.userDailyLossEnabled);
  pushUsd('亏损平仓额', current.userDailyLossCloseUsd, pending.userDailyLossCloseUsd);
  pushUsd('亏损冻结额', current.userDailyLossFreezeUsd, pending.userDailyLossFreezeUsd);
  pushBool('逐用户盈利上限', current.userDailyProfitEnabled, pending.userDailyProfitEnabled);
  pushUsd('盈利平仓额', current.userDailyProfitCloseUsd, pending.userDailyProfitCloseUsd);
  pushUsd('盈利冻结额', current.userDailyProfitFreezeUsd, pending.userDailyProfitFreezeUsd);
  pushNum('最大持仓(手)', current.userMaxPositionLots100 / 100, pending.userMaxPositionLots100 == null ? undefined : pending.userMaxPositionLots100 / 100, ' 手', 2, '不限');
  pushNum('每日最多笔数', current.userDailyMaxTrades, pending.userDailyMaxTrades, ' 笔', 0, '不限');
  pushUsd('手动平仓门槛', current.manualCloseMinProfitUsd, pending.manualCloseMinProfitUsd);

  if (rows.length === 0) return <div className="text-xs">(暂无字段差异显示)</div>;
  return (
    <div className="space-y-0.5 text-xs num">
      {rows.map(([label, before, after]) => (
        <div key={label}>
          {label}:<span className="text-ink-2 line-through ml-1">{before}</span>
          <span className="mx-1.5 text-ink-3">→</span>
          <span className="text-warn font-semibold">{after}</span>
        </div>
      ))}
    </div>
  );
}

interface SliderRowProps {
  label: string;
  hint?: string;
  unit: string;
  min: number;
  max: number;
  step: number;
  value: number;
  onChange: (v: number) => void;
  decimals?: number;
  zeroLabel?: string;
}

function SliderRow({ label, hint, unit, min, max, step, value, onChange, decimals = 0, zeroLabel }: SliderRowProps) {
  const displayValue = zeroLabel && value <= 0 ? zeroLabel : value.toFixed(decimals);
  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline justify-between">
        <Label>{label}</Label>
        <div className="flex items-baseline gap-1.5 num">
          <span className="text-lg font-bold text-ink-0">{displayValue}</span>
          {!(zeroLabel && value <= 0) && <span className="text-xs text-ink-3">{unit}</span>}
        </div>
      </div>
      <Slider value={value} min={min} max={max} step={step} onChange={onChange} />
      <div className="flex items-center justify-between text-[11px] text-ink-3">
        <span className="num">{min} - {max} {unit}</span>
        {hint && <span>{hint}</span>}
      </div>
    </div>
  );
}
